// docs/31 in the encounter HUD: fog of war. The FIELD panel says why the field is veiled and
// what the selected hero's eyes can do there; under the foe list, the foes the party has lost
// sight of ("last seen 2 turns ago") or only heard; the foe's card says whether it is prowling.
// Every word is the sim's (`FogView`, `MindNote`); this file only lays them out.
import { HeroSprite } from '../components';
import { useShownSnapshot } from '../../core/encounter/session';
import { useAuthoring } from '../../core/stores/authoring';
import type { CardCount, FogMark, MindNote } from '../../core/sim/types';

/** The fog, if this fight has one (the board's snapshot: what the scene draws, docs/56 as it plays). */
function useFog() {
  return useShownSnapshot()?.fog;
}

/** FIELD rows: each veil in a line, then the selected hero's senses ("sight 7 · torch 3"). */
export function FogRows() {
  const fog = useFog();
  const snapshot = useShownSnapshot();
  const selected = useAuthoring((s) => s.selectedHero);
  if (!fog) return null;
  const hero = snapshot?.units.find((u) => u.heroId !== undefined && u.heroId === selected) ?? snapshot?.units.find((u) => u.heroId !== undefined);
  const eyes = hero ? fog.eyes.find((e) => e.unit === hero.unitId) : undefined;
  return (
    <div className="fk-fog" data-testid="hud-fog" data-veils={fog.veils.map((v) => v.key).join(',')}>
      <p className="fk-fog__title"><span className="fk-fog__glyph" aria-hidden>◐</span> Fog of war</p>
      <ul className="fk-fog__veils">
        {fog.veils.map((v) => (
          <li key={v.key} className={`fk-fog__veil fk-fog__veil--${v.key}`}><b>{v.name}</b> {v.line}</li>
        ))}
        {!fog.veils.length && <li className="fk-fog__veil">Each side sees only what its own eyes reach.</li>}
      </ul>
      {/* docs/63: behind closed doors sight is the rooms, not a hero's eyes */}
      {hero && eyes && !fog.veils.some((v) => v.key === 'rooms') && (
        <p className="fk-fog__eyes" data-testid="hud-eyes" data-unit-id={hero.unitId} title={eyes.words.join('\n')}>
          <b>{hero.name}</b> {eyes.words.join(' · ')}
        </p>
      )}
    </div>
  );
}

function markLine(m: FogMark, now: number): string {
  const ago = Math.max(0, now - m.pulse);
  const when = ago === 0 ? 'this turn' : ago === 1 ? '1 turn ago' : `${ago} turns ago`;
  if (m.sense === 'seen') return `last seen ${when}`;
  if (m.sense === 'heard') return `heard fighting ${when}`;
  return `scented ${when}`;
}

/** OUT OF SIGHT: foes the party remembers or heard, under the foe list. */
export function LostFoes() {
  const fog = useFog();
  if (!fog || !fog.marks.length) return null;
  const marks = [...fog.marks].sort((a, b) => b.pulse - a.pulse || a.unit - b.unit);
  return (
    <div className="fk-fog__lost" data-testid="lost-foes" aria-label="Foes out of sight">
      <span className="fk-hud__label">Out of sight · {marks.length}</span>
      <ul>
        {marks.map((m) => (
          <li key={`${m.unit}:${m.pulse}`} className={`fk-fog__mark fk-fog__mark--${m.sense}`} data-testid="lost-foe" data-unit-id={m.unit}>
            <span className="fk-fog__mark-face">{m.look ? <HeroSprite frame={m.look} scale={2} /> : <span aria-hidden>{m.sense === 'heard' ? '♪' : '∴'}</span>}</span>
            <span>{markLine(m, fog.pulse)} <span className="fk-muted">({m.tile.x},{m.tile.y})</span></span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** True when the fight has fog and no foe is in sight (the foe list says so instead of "Foes · 0"). */
export function useNoFoeInSight(foes: number): boolean {
  const fog = useFog();
  return !!fog && foes === 0;
}

/** The mind card's fog block: prowling or hunting, its senses, its prowl deck. */
export function MindFog({ mind }: { mind: MindNote }) {
  if (!mind.aware && !mind.prowl.length) return null;
  const total = mind.prowl.reduce((s, c: CardCount) => s + c.count, 0);
  return (
    <div className="fk-mind__fog" data-testid="mind-fog">
      {mind.aware && (
        <p className={`fk-mind__aware fk-mind__aware--${mind.aware.name.toLowerCase()}`} data-testid="mind-aware" title={mind.aware.line}>
          <b>{mind.aware.name}</b> <span className="fk-muted">{mind.aware.line}</span>
        </p>
      )}
      {mind.senses.length > 0 && <p className="fk-mind__line fk-muted" data-testid="mind-senses">{mind.senses.join(' · ')}</p>}
      {mind.prowl.length > 0 && (
        <div className="fk-mind__deck" aria-label={`Prowl deck, ${total} cards`}>
          <span className="fk-hud__label">Prowl deck · {total} <span className="fk-muted">(when it knows of no foe)</span></span>
          <ul>
            {mind.prowl.map((c) => (
              <li key={c.name} className="fk-mind__card fk-mind__card--prowl" title={c.line} tabIndex={0}>
                <span className="fk-mind__card-name">{c.name}</span>
                <span className="fk-mind__card-count">×{c.count}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
